import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import API from "../API";

const money = (cents) =>
  (Number(cents || 0) / 100).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
  });
const status = {
  paid: "Ödendi",
  awaiting_payment: "Ödeme Bekleniyor",
  overdue: "Vadesi Geçti",
};
export default function JyskFinanceScreen() {
  const [ctx, setCtx] = useState(null);
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState(null);
  const [settings, setSettings] = useState(null);
  const [employee, setEmployee] = useState({
    employeeId: "",
    name: "",
    storeCode: "",
  });
  const [user, setUser] = useState({
    userId: "",
    parentUserId: "",
    role: "store",
    storeCode: "",
  });
  const [notice, setNotice] = useState("");
  const call = async (path, body = {}) => {
    const data = await API.fetch(`jysk/${path}`, body);
    if (data.error) throw new Error(data.message || "İşlem tamamlanamadı.");
    return data;
  };
  const load = async () => {
    const [context, invoices] = await Promise.all([
      call("context"),
      call("invoices"),
    ]);
    setCtx(context);
    setRows(invoices.rows);
  };
  const run = async (fn) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    run(load);
  }, []);
  const field = (label, value, onChange, type = "text") => (
    <TextField
      label={label}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      InputLabelProps={{ shrink: true }}
    />
  );
  return (
    <Stack spacing={3}>
      <Typography variant="h4">JYSK finans ve yetkiler</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Alert severity="info">
        JYSK finans kullanıcıları mevcut kurumsal giriş ekranından giriş yapar
        ve tüm JYSK mağazalarını salt okunur görüntüler. Faturalar tamamlanan
        siparişlerden oluşturulur; vade takip eden ayın sonudur.
      </Alert>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Aylık fatura oluştur</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {field("Fatura dönemi", period, setPeriod, "month")}
            <Button
              variant="contained"
              disabled={busy || !period}
              onClick={() =>
                run(async () => {
                  await call("issue-invoice", { period });
                  await load();
                  setNotice("Aylık fatura oluşturuldu.");
                })
              }
            >
              Faturayı oluştur
            </Button>
            <Button disabled={busy} onClick={() => run(load)}>
              Yenile
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Paper
        variant="outlined"
        sx={{ p: 3, borderRadius: 3, overflow: "auto" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {[
                "Fatura",
                "Dönem",
                "Siparişler",
                "Tutar",
                "Vade",
                "Durum",
                "İşlem",
              ].map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row._id}>
                <TableCell>{row._id}</TableCell>
                <TableCell>{row.period}</TableCell>
                <TableCell>
                  <Box component="details">
                    <summary>{row.orderIds.length} sipariş</summary>
                    {row.orderIds.map((id) => (
                      <Button key={id} href={`/order/${id}`}>
                        #{String(id).slice(-6).toUpperCase()}
                      </Button>
                    ))}
                  </Box>
                </TableCell>
                <TableCell>{money(row.amountCents)}</TableCell>
                <TableCell>{row.due}</TableCell>
                <TableCell>
                  {status[row.paymentStatus]}
                  {row.payment && (
                    <Typography variant="caption" display="block">
                      {row.payment.date} · {row.payment.reference}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {!row.payment && (
                    <Button
                      disabled={busy}
                      onClick={() =>
                        setPayment({
                          id: row._id,
                          amount: String(row.amountCents / 100),
                          date: new Date().toLocaleDateString("sv-SE"),
                          reference: "",
                        })
                      }
                    >
                      Tam ödeme kaydet
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!rows.length && (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Henüz aylık fatura yok.
          </Typography>
        )}
      </Paper>
      {payment && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">{payment.id} · Tam ödeme kaydı</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {field(
                "Ödenen tutar",
                payment.amount,
                (amount) => setPayment((p) => ({ ...p, amount })),
                "number",
              )}
              {field(
                "Ödeme tarihi",
                payment.date,
                (date) => setPayment((p) => ({ ...p, date })),
                "date",
              )}
              {field("Ödeme referansı", payment.reference, (reference) =>
                setPayment((p) => ({ ...p, reference })),
              )}
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                disabled={busy || !payment.reference}
                onClick={() =>
                  run(async () => {
                    await call("record-payment", payment);
                    setPayment(null);
                    await load();
                    setNotice(
                      "Fatura ve bağlı siparişler ödendi olarak güncellendi.",
                    );
                  })
                }
              >
                Ödemeyi kaydet
              </Button>
              <Button onClick={() => setPayment(null)}>Vazgeç</Button>
            </Stack>
          </Stack>
        </Paper>
      )}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">
            Mağaza yetkileri ve çalışan kayıtları
          </Typography>
          <Typography color="text.secondary">
            Mevcut kurumsal kullanıcı hesabını seçerek mağaza, yönetici veya
            finans yetkisi atayın. Giriş bilgileri mevcut kurumsal kullanıcı
            yönetiminden oluşturulur.
          </Typography>
          <Button
            disabled={busy}
            onClick={() => run(async () => setSettings(await call("settings")))}
          >
            Yetki ve çalışan kayıtlarını aç
          </Button>
          {settings && (
            <>
              {!settings.stores && (
                <Alert severity="warning">
                  Mağaza etkinleştirme ve bağlı yönetici hesapları için
                  sunucunun güncel sürümünü yükleyin.
                </Alert>
              )}
              <Typography variant="subtitle1">
                Yeni mağaza akışını etkinleştir
              </Typography>
              <Typography color="text.secondary">
                Başlangıçta yalnızca T003 ve T011 etkindir. Finans her zaman tüm
                mağazaları görür.
              </Typography>
              {settings.stores?.map((st) => (
                <Stack
                  key={st.storeId}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  <Typography>
                    {st.storeId} · {st.label} ·{" "}
                    {st.workflowEnabled ? "Etkin" : "Kapalı"}
                  </Typography>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        setSettings(
                          await call("settings", {
                            workflowStoreCode: st.storeId,
                            workflowEnabled: !st.workflowEnabled,
                          }),
                        );
                        await load();
                      })
                    }
                  >
                    {st.workflowEnabled ? "Devre dışı bırak" : "Etkinleştir"}
                  </Button>
                </Stack>
              ))}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Kullanıcı"
                  value={user.userId}
                  onChange={(e) =>
                    setUser(() => {
                      const u = settings.users.find(
                        (item) => item._id === e.target.value,
                      );
                      return {
                        userId: u._id,
                        role: u.jyskRole || "store",
                        storeCode: u.jyskStoreCode || "",
                        parentUserId: u.jyskParentUserId || "",
                      };
                    })
                  }
                  sx={{ minWidth: 240 }}
                >
                  {settings.users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.name} · {u.email} · {u.jyskRole || "store"}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Yetki"
                  value={user.role}
                  onChange={(e) =>
                    setUser((v) => ({ ...v, role: e.target.value }))
                  }
                >
                  {[
                    ["store", "Mağaza"],
                    ["manager", "Yetkili mağaza yöneticisi"],
                    ["finance", "Finans · Salt okunur"],
                  ].map(([key, name]) => (
                    <MenuItem key={key} value={key}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Mağaza"
                  value={user.storeCode}
                  onChange={(e) =>
                    setUser((v) => ({ ...v, storeCode: e.target.value }))
                  }
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">Tümü (yalnızca finans)</MenuItem>
                  {ctx?.stores.map((st) => (
                    <MenuItem key={st.storeId} value={st.storeId}>
                      {st.storeId} · {st.label}
                    </MenuItem>
                  ))}
                </TextField>
                {user.role === "manager" && (
                  <TextField
                    select
                    size="small"
                    label="Bağlı mağaza hesabı"
                    value={user.parentUserId}
                    sx={{ minWidth: 240 }}
                    onChange={(e) =>
                      setUser((v) => ({ ...v, parentUserId: e.target.value }))
                    }
                  >
                    <MenuItem value="">Mağaza hesabını seçin</MenuItem>
                    {settings.users
                      .filter(
                        (u) =>
                          u._id !== user.userId &&
                          (u.jyskRole || "store") === "store" &&
                          u.jyskStoreCode === user.storeCode,
                      )
                      .map((u) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.name} · {u.email}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
                <Button
                  disabled={busy || !user.userId || !settings.stores}
                  onClick={() =>
                    run(async () => {
                      setSettings(await call("settings", user));
                      setNotice("Kullanıcı yetkisi güncellendi.");
                    })
                  }
                >
                  Yetkiyi kaydet
                </Button>
              </Stack>
              <Typography variant="subtitle1">
                Çalışan ekle / güncelle
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                {field("Çalışan numarası", employee.employeeId, (employeeId) =>
                  setEmployee((v) => ({ ...v, employeeId })),
                )}
                {field("Ad soyad", employee.name, (name) =>
                  setEmployee((v) => ({ ...v, name })),
                )}
                <TextField
                  select
                  size="small"
                  label="Mağaza"
                  value={employee.storeCode}
                  onChange={(e) =>
                    setEmployee((v) => ({ ...v, storeCode: e.target.value }))
                  }
                  sx={{ minWidth: 180 }}
                >
                  {ctx?.stores.map((st) => (
                    <MenuItem key={st.storeId} value={st.storeId}>
                      {st.storeId} · {st.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  disabled={
                    busy ||
                    !employee.employeeId ||
                    !employee.name ||
                    !employee.storeCode
                  }
                  onClick={() =>
                    run(async () => {
                      setSettings(await call("settings", employee));
                      setNotice("Çalışan kaydedildi.");
                    })
                  }
                >
                  Çalışanı kaydet
                </Button>
              </Stack>
              {settings.employees.map((e) => (
                <Stack
                  key={e._id}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  <Typography>
                    {e.storeCode} · {e.employeeId} · {e.name} ·{" "}
                    {e.active ? "Aktif" : "Pasif"}
                  </Typography>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      run(async () =>
                        setSettings(
                          await call("settings", { ...e, active: !e.active }),
                        ),
                      )
                    }
                  >
                    {e.active ? "Pasifleştir" : "Etkinleştir"}
                  </Button>
                </Stack>
              ))}
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
